/**
 * A one-off confetti burst over the whole window, for finishing something worth celebrating.
 * Drawn on a temporary canvas that removes itself; skipped for people who asked for reduced motion.
 */

const COLORS = ["#000080", "#1E4FB4", "#7FA2FF", "#1F7A4D", "#34A853", "#FBBC05", "#EA4335", "#FFFFFF"];
const DURATION_MS = 2600;

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  spin: number;
  angle: number;
  round: boolean;
}

export function confettiBurst(origin?: { x: number; y: number }) {
  if (typeof window === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  canvas.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:9999";
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas.remove();
  ctx.scale(dpr, dpr);

  const ox = origin?.x ?? window.innerWidth / 2;
  const oy = origin?.y ?? window.innerHeight / 3;
  // Two side cannons plus a pop from the origin, like a party popper
  const pieces: Piece[] = [];
  const add = (x: number, y: number, count: number, angleFrom: number, angleTo: number, speed: number) => {
    for (let i = 0; i < count; i++) {
      const a = angleFrom + Math.random() * (angleTo - angleFrom);
      const v = speed * (0.55 + Math.random() * 0.6);
      pieces.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        size: 6 + Math.random() * 6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
        spin: (Math.random() - 0.5) * 0.4,
        angle: Math.random() * Math.PI,
        round: Math.random() < 0.3,
      });
    }
  };
  add(ox, oy, 90, 0, Math.PI * 2, 13);
  add(0, window.innerHeight, 60, -Math.PI / 2.4, -Math.PI / 6, 22);
  add(window.innerWidth, window.innerHeight, 60, -Math.PI + Math.PI / 6, -Math.PI + Math.PI / 2.4, 22);

  const startedAt = performance.now();
  const frame = (now: number) => {
    const t = now - startedAt;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    const fade = Math.min(1, Math.max(0, (DURATION_MS - t) / 600));
    for (const p of pieces) {
      p.vy += 0.38;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.angle += p.spin;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Flipping paper: the width shrinks and grows as it turns
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size * Math.abs(Math.cos(p.angle * 2)), p.size / 2);
      }
      ctx.restore();
    }
    if (t < DURATION_MS) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}
